import { TCAppConfig } from "../../../../../shared/app-configs/tc-app-configs";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { InputData, Payload, TcShipmentConsigneeDetail } from "./shipments.model";
/**
 * Fetch shipment details using the given API caller.
 *
 * @param executeGet - The API call function from your custom hook.
 * @param tcId - The TE_ID or other identifier.
 * @param shipmentId - The shipment identifier.
 * @returns A promise that resolves with the shipment details.
 * @throws An error if the fetch fails.
 */
export async function fetchShipmentDetails(
    executeGet: (url: string, params: Record<string, any>) => Promise<any>,
    tcId: string,
    shipmentId: string
  ): Promise<any> {
    if (!shipmentId) return null;
  
    try {
      const response = await executeGet(TCAppConfig.GET_SHIPMENTS_NEW, {
        tcId,
        shipmentId,
      });
      return response;
    } catch (err) {
      console.error('Error fetching shipment details:', err);
      throw new Error('Failed to load shipment details.');
    }
  }
 /**
 * Maps input data to the desired payload structure.
 *
 * @param data - The input data.
 * @param tcId - The top-level tcId.
 * @param shipmentId - Optional shipmentId to be added when provided.
 * @returns The mapped payload.
 */
export const createPayload = (data:InputData, tcId:string, shipmentId?:string|null): Payload => {
  return {
    // Top-level properties
    tcId: tcId, // Set a default or generate as needed
    tcGrossShippingWeight: Number(data.tcShipmentGrossShippingWeight),
    tcShipmentConsigneeDetails: [
      {
        // Shipment consignee details mapped from input data
        tcShipmentConsigneeOrder: "0", // Default value; update as required
        tcShipmentNo: data.tcShipmentNo,
        tcShipmentDate: data.tcShipmentDate||"",
        tcShipmentDocNo: data.tcShipmentDocNo||"",
        // Assuming the shipment gross weight is the same as the overall gross weight
        //tcShipmentGrossShippingWeight
        tcShipmentGrossShippingWeight: Number(data.tcShipmentGrossShippingWeight)||0,
        tcShipmentInvoiceReferences: data.tcShipmentInvoiceReferences||[],
        consigneeName: data.consigneeName||"",
        consigneeTeId: data.consigneeTeId||"",
        isConsigneeTeCertified: data.isConsigneeTeCertified||"",
        consigneeAddress1: data.consigneeAddress1||"",
        consigneeAddress2: data.consigneeAddress2||"",
        consigneeAddress3: data.consigneeAddress3||"",
        consigneePostcode: data.consigneePostcode||"",
        consigneeTown: data.consigneeTown||"",
        consigneeStateOrProvince: data.consigneeStateOrProvince||"",

        consigneeCountryOrArea: data.consigneeCountryOrArea||"",
        consigneeCountryOrAreaDescription: data.consigneeCountryOrArea ? useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === data.consigneeCountryOrArea)?.label || "" : "",
        consigneeStateOrProvinceDescription: data.consigneeStateOrProvince ? useTransactionCertificateStore.getState().getStates.find((option: any) => option.value === data.consigneeStateOrProvince)?.label || "" : "",
        additionalProp1: {} // Additional properties can be added here
      }
    ],
    additionalProp1: {}, // Top-level additional properties if needed
    ...(shipmentId ? { shipmentId } : {})
  };
};

/**
 * Maps input data to a TcShipmentConsigneeDetail object.
 *
 * @param data - The input data.
 * @param order - (Optional) The shipment consignee order (default "0").
 * @returns The mapped TcShipmentConsigneeDetail object.
 */
export const mapInputDataToTcShipmentConsigneeDetail = (
  data: InputData,
): TcShipmentConsigneeDetail => {
  return {
    tcShipmentConsigneeOrder: "0",
    tcShipmentNo: data.tcShipmentNo,
    tcShipmentDate: data.tcShipmentDate,
    tcShipmentDocNo: data.tcShipmentDocNo,
    tcShipmentGrossShippingWeight: Number(data.tcShipmentGrossShippingWeight),
    tcShipmentInvoiceReferences: data.tcShipmentInvoiceReferences,
    consigneeName: data.consigneeName,
    consigneeTeId: data.consigneeTeId,
    // Ensure the value is either "Y" or "N"
    isConsigneeTeCertified: data.isConsigneeTeCertified === "Y" ? "Y" : "N",
    consigneeAddress1: data.consigneeAddress1,
    consigneeAddress2: data.consigneeAddress2,
    consigneeAddress3: data.consigneeAddress3,
    consigneePostcode: data.consigneePostcode,
    consigneeTown: data.consigneeTown,
    consigneeStateOrProvince: data.consigneeStateOrProvince,
    consigneeCountryOrArea: data.consigneeCountryOrArea,
  };
};
